import * as localForage from 'localforage';
import { LOCAL_STORAGE, StorageService } from 'ngx-webstorage-service';

import { Inject, Injectable, inject } from '@angular/core';
import { NativeStorage } from '@ionic-native/native-storage/ngx';
import { Platform } from '@ionic/angular';
import { CacheBucket } from '@ngneat/cashew';
import { CACHE_TIMEOUT } from '@shared/constants';
import { BehaviorSubject } from 'rxjs';
import { CompanyFacilityService } from '@shared/facility-services/company.facility.service';

@Injectable({ providedIn: 'root' })
export class CacheService {
	private isNative = false; // this.platform.is('cordova');
	public buckets = {
		products: new CacheBucket(),
		reservation: new CacheBucket(),
		insurance: new CacheBucket(),
	};
	private _apiCache: LocalForage;
	public companyIdSet = new BehaviorSubject<boolean>(false);
	private companyFacilityService = inject(CompanyFacilityService);

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		private readonly storage: NativeStorage,
		@Inject(LOCAL_STORAGE) private webStorage: StorageService,
		private platform: Platform
	) {
		this._apiCache = localForage.createInstance({
			driver: [localForage.INDEXEDDB, localForage.WEBSQL],
			name: `VQUIP_API_CACHE`,
			version: 1.0,
			size: 4980736,
			storeName: `api_cache`,
			description: 'vQuip API Store',
		});
		this.get('secure:company_id').then((companyId) => {
			if (companyId) { this.companyIdSet.next(true); }
		});
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME -
	 **-------------------------------------------------------------------------------------
	 */
	get apiCache() {
		return this._apiCache;
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - setAPICacheTimeout
	 **-------------------------------------------------------------------------------------
	 */
	public setAPICacheTimeout() {
		this._apiCache.getItem('INITIALIZED').then(exp => {
			if (!exp) {
				this._apiCache.setItem('INITIALIZED', new Date().getTime());
				this._apiCache.setItem('EXPIRE_AT', new Date().setMilliseconds(CACHE_TIMEOUT));
			}
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - isApiCacheExpired
	 **-------------------------------------------------------------------------------------
	 */
	public async isApiCacheExpired() {
		const initialized = await new Date().getTime();
		const expiredAt = (await this._apiCache.getItem('EXPIRE_AT')) as number;
		const isValid = expiredAt > initialized;
		if (!isValid) {
			console.log('Clearing api cache');
			this._apiCache.clear().then(() => this.setAPICacheTimeout());
		}
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - set
	 **-------------------------------------------------------------------------------------
	 */
	public set(key: string, value: any) {
		return new Promise((resolve, reject) => {
			if (!this.isNative) return resolve(this.webStorage.set(key, value));
			this.storage.setItem(key, value).then((result) => {
				resolve(result);
			}).catch(err => reject(err));
		}).finally(() => {
			if (key === 'secure:company_id') { 
				this.companyFacilityService.setCompanyId(value);
				this.companyIdSet.next(true); 
			}
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - get
	 **-------------------------------------------------------------------------------------
	 */
	public async get(key: string, defaultValue: string = null) {
		if (!this.isNative) return Promise.resolve(this.webStorage.get(key) || defaultValue);
		return this.storage.getItem(key);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - delete
	 **-------------------------------------------------------------------------------------
	 */
	public async delete(key: string) {
		if (key === 'secure:company_id') { this.companyIdSet.next(false); }
		if (!this.isNative) return Promise.resolve(this.webStorage.remove(key));
		return this.storage.remove(key);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - clear
	 **-------------------------------------------------------------------------------------
	 */
	public async clear() {
		this.companyIdSet.next(false);
		if (!this.isNative) return Promise.resolve(this.webStorage.clear());
		return this.storage.clear();
	}
}
